import { useMutation } from '@tanstack/react-query'
import { ActionBar, Dialog, Modal, RolesIcon, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../../components/GenericTable'
import { useToast } from '../../../../utils/ToastContext'
import { queryClient, trpc } from '../../../../utils/trpc'
import type { Role, RoleListInput } from '../../../roles/types'
import type { CycleRole, CycleRoleListInput } from '../types'

const CycleAddRoles = () => {
	const navigate = useNavigate()
	const { cycleId, organizationId } = useParams<{ cycleId: string; organizationId: string }>()
	const { markStepCompleted } = useOutletContext<{ markStepCompleted: (idx: number) => void }>()
	const [addRolesModalOpen, setAddRolesModalOpen] = useState(false)
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [selectedCycleRoles, setSelectedCycleRoles] = useState<CycleRole[]>([])
	const [selectedRoles, setSelectedRoles] = useState<Role[]>([])

	const toast = useToast()

	const columns: Column<CycleRole>[] = [
		{
			field: 'name',
			label: 'Display Name',
			colSpan: 2,
			render: (role: CycleRole) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.name}
				</Text>
			),
		},
		{
			field: 'displayName',
			label: 'Role Display Name',
			colSpan: 2,
			render: (role: CycleRole) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.displayName}
				</Text>
			),
		},
		{
			field: 'description',
			label: 'Description',
			colSpan: 2,
			render: (role: CycleRole) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.description}
				</Text>
			),
		},
	]

	const roleColumns: Column<Role>[] = [
		{
			field: 'name',
			label: 'Role Name',
			colSpan: 2,
			render: (role: Role) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.name}
				</Text>
			),
		},
		{
			field: 'displayName',
			label: 'Display Name',
			colSpan: 2,
			render: (role: Role) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.displayName}
				</Text>
			),
		},
		{
			field: 'description',
			label: 'Description',
			colSpan: 2,
			render: (role: Role) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.description}
				</Text>
			),
		},
	]

	const editRoleMappingMutation = useMutation({
		...trpc.admin.cycles.roles.mappings.edit.mutationOptions({
			onSuccess: () => {
				toast.showToast('Cycle roles added')
				setAddRolesModalOpen(false)
				setSelectedRoles([])
			},
			onError: () => {
				toast.showToast('Error adding cycle roles', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.roles.list.queryKey({ cycleId: cycleId as string }),
					exact: false,
					refetchType: 'all',
				})
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.roles.listPossible.queryKey({
						cycleId: cycleId as string,
						organizationId: organizationId as string,
					}),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const deleteCycleRolesMutation = useMutation({
		...trpc.admin.cycles.roles.mappings.delete.mutationOptions({
			onSuccess: () => {
				toast.showToast('Cycle roles deleted')
				setIsDeleteDialogOpen(false)
				setSelectedRoles([])
			},
			onError: () => {
				toast.showToast('Error deleting cycle roles', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.roles.list.queryKey({ cycleId: cycleId as string }),
					exact: false,
					refetchType: 'all',
				})
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.roles.listPossible.queryKey({
						cycleId: cycleId as string,
						organizationId: organizationId as string,
					}),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const handleAddRoles = async () => {
		if (!cycleId || !selectedRoles.length) return

		const roles = selectedRoles.map((role: Role | CycleRole) => ({
			roleId: role.id,
		}))

		if (roles.length === 0) return

		await editRoleMappingMutation.mutateAsync({
			cycleId,
			roles: roles as [(typeof roles)[0], ...typeof roles],
		})
	}

	const handleDeleteSelected = () => {
		if (!cycleId || !selectedCycleRoles.length) return
		deleteCycleRolesMutation.mutate({
			cycleId: cycleId,
			ids: selectedCycleRoles.map((role) => role.id) as [string, ...string[]],
		})
	}

	const handleOpenDeleteDialog = () => {
		setIsDeleteDialogOpen(true)
	}

	const handleCloseDeleteDialog = () => {
		setIsDeleteDialogOpen(false)
	}

	useEffect(() => {
		if (!cycleId || !organizationId) return
		if (!addRolesModalOpen) return
		queryClient.invalidateQueries({
			queryKey: [['cyclesRoles']],
			exact: false,
			refetchType: 'all',
		})
	}, [cycleId, organizationId, addRolesModalOpen])

	return (
		<div>
			<GenericTable<CycleRole, CycleRoleListInput>
				columns={columns}
				idField='id'
				selectable
				isInternal
				onSelectionChange={setSelectedCycleRoles}
				queryOptionsGenerator={(options) => ({
					...trpc.admin.cycles.roles.list.queryOptions({
						...options,
						cycleId: cycleId as string,
					}),
				})}
				showSearch
				showPagination
				actionButtons={[
					{
						children: 'Add roles',
						onClick: () => setAddRolesModalOpen(true),
						variant: 'primary' as const,
					},
				]}
				emptyState={{
					icon: RolesIcon,
					title: 'Cycle Roles',
					description: 'Get started by adding a new role',
					actionLabel: 'Add cycle roles',
					onAction: () => {
						setAddRolesModalOpen(true)
					},
				}}
			/>
			<Modal
				isOpen={addRolesModalOpen}
				fullContent
				onClose={() => {
					setAddRolesModalOpen(false)
					setSelectedRoles([])
				}}
				title='Add cycle roles'
				fullScreen>
				<GenericTable<Role, RoleListInput>
					columns={roleColumns}
					idField='id'
					selectable
					isInternal
					onSelectionChange={setSelectedRoles}
					queryOptionsGenerator={(options) => ({
						...trpc.admin.cycles.roles.listPossible.queryOptions({
							...options,
							cycleId: cycleId as string,
							organizationId: organizationId as string,
						}),
					})}
					showSearch
					showPagination
					emptyState={{
						icon: RolesIcon,
						title: 'Form Roles',
						description: 'All roles added to cycle',
					}}
				/>
				<ActionBar
					variant='modal'
					rightActions={[
						{
							variant: 'secondary',
							label: 'Cancel',
							onClick: () => setAddRolesModalOpen(false),
						},
						{
							variant: 'primary',
							label: `Add roles (${selectedRoles.length})`,
							onClick: handleAddRoles,
							disabled: selectedRoles.length === 0,
						},
					]}
				/>
			</Modal>

			{selectedCycleRoles.length > 0 ? (
				<ActionBar
					rightActions={[
						{
							label: 'Cancel',
							variant: 'secondary',
							onClick: () => setSelectedCycleRoles([]),
						},
						{
							label: `Delete${selectedCycleRoles.length > 0 ? ` (${selectedCycleRoles.length})` : ''}`,
							variant: 'destructive',
							onClick: handleOpenDeleteDialog,
						},
					]}
				/>
			) : (
				<ActionBar
					rightActions={[
						{
							label: 'Cancel',
							variant: 'secondary',
							onClick: () => setSelectedCycleRoles([]),
						},
						{
							label: 'Next',
							variant: 'primary',
							onClick: () => {
								markStepCompleted(0)
								navigate(`/organizations/${organizationId}/cycles/${cycleId}/roles/mapRoles`)
							},
						},
					]}
				/>
			)}

			<Dialog
				isOpen={isDeleteDialogOpen}
				onClose={handleCloseDeleteDialog}
				onConfirm={handleDeleteSelected}
				title='Delete cycle roles'
				message={`Are you sure you want to delete ${selectedCycleRoles.length} cycle role${selectedCycleRoles.length === 1 ? '' : 's'}?`}
				type='delete'
				confirmLabel='Delete'
			/>
		</div>
	)
}

export default CycleAddRoles
